/**
 * 법률 페이지 공통 레이아웃 컴포넌트
 *
 * 이용약관, 개인정보 보호정책 등 법률 문서 페이지의 공통 레이아웃을 제공합니다.
 * - 고정 헤더 (뒤로가기 + 제목)
 * - 스크롤 가능한 콘텐츠 영역
 * - 연락처 정보 카드
 */

import type { ReactNode } from 'react';
import type { LegalSection as LegalSectionType } from './types';
import { contactTranslations } from './constants';
import { LegalSectionCard } from './LegalSectionCard';
import { useLanguage } from '@/contexts/AppContext';

interface LegalPageLayoutProps {
  /** 페이지 제목 */
  title: string;
  /** 최종 업데이트 일자 */
  lastUpdated: string;
  /** 소개 텍스트 (옵션) */
  intro?: string;
  /** 섹션 목록 */
  sections: LegalSectionType[];
  /** 뒤로가기 핸들러 */
  onBack: () => void;
  /** 문단 들여쓰기 판별 함수 (옵션) */
  shouldIndent?: (paragraph: string) => boolean;
  /** 추가 콘텐츠 (옵션) */
  children?: ReactNode;
}

export function LegalPageLayout({
  title,
  lastUpdated,
  intro,
  sections,
  onBack,
  shouldIndent,
  children,
}: LegalPageLayoutProps) {
  const { language } = useLanguage();
  return (
    <div
      className="min-h-screen bg-muted"
      style={{ fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif" }}
    >
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-primary h-[50px]">
        <div className="flex items-center justify-center relative h-full px-[20px]">
          <button
            onClick={onBack}
            className="absolute left-[20px] flex items-center justify-center w-[32px] h-[32px]"
          >
            <svg
              className="w-[12px] h-[21px]"
              fill="none"
              preserveAspectRatio="none"
              viewBox="0 0 12 21"
            >
              <path
                d="M11.588 0.41098C11.4583 0.280705 11.3042 0.177346 11.1345 0.106823C10.9649 0.0363008 10.783 0 10.5993 0C10.4156 0 10.2337 0.0363008 10.0641 0.106823C9.89442 0.177346 9.74031 0.280705 9.61059 0.41098L0.326717 9.71087C0.223149 9.8144 0.140982 9.93738 0.0849197 10.0728C0.0288573 10.2082 0 10.3533 0 10.4999C0 10.6464 0.0288573 10.7916 0.0849197 10.9269C0.140982 11.0623 0.223149 11.1853 0.326717 11.2888L9.61059 20.5887C10.158 21.1371 11.0406 21.1371 11.588 20.5887C12.1354 20.0404 12.1354 19.1562 11.588 18.6079L3.49955 10.4943L11.5992 2.38063C12.1354 1.84345 12.1354 0.948157 11.588 0.41098Z"
                fill="white"
              />
            </svg>
          </button>
          <h1
            className="font-['Noto_Sans:Bold',_'Noto_Sans_KR:Bold',_sans-serif] font-bold text-[16px] text-white tracking-[-0.32px] text-center uppercase"
            style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
          >
            {title}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="pt-[50px]">
        <div className="max-w-4xl mx-auto px-4 py-6 pb-20">
          {/* Last Updated */}
          <div className="mb-6 text-center">
            <p className="text-muted-foreground text-sm">
              {lastUpdated}
            </p>
          </div>

          {/* Introduction (optional) */}
          {intro && (
            <div className="mb-6 bg-primary/10 rounded-lg p-5 border border-primary/30">
              <p className="text-foreground text-sm leading-relaxed">
                {intro}
              </p>
            </div>
          )}

          {/* Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <LegalSectionCard
                key={index}
                section={section}
                shouldIndent={shouldIndent}
              />
            ))}
          </div>

          {/* Additional Content */}
          {children}

          {/* Contact Info */}
          <div className="mt-8 bg-primary/10 rounded-lg p-5 border border-primary/30">
            <p className="text-foreground text-sm text-center">
              {contactTranslations[language]}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
